import { Tooltip } from "antd";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { useTranslation } from "react-i18next";
import { KbdChip } from "../modalPrimitives";
import {
  OPTIONS_ZONE_ID,
  type FooterDragData,
} from "./footer/footerPinning";

/** Вторично действие от „Опции" (сторно, раздели, клиенти, отчет…). */
export interface SecondaryAction {
  key: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  shortcut?: string;
  /** Подсказка, когато действието е недостъпно в локален режим (без
   *  интернет, docs/edge.md) — бутонът стои, но е disabled. */
  lockedTooltip?: string;
}

interface OptionsPopoverProps {
  actions: SecondaryAction[];
  isNarrow: boolean;
  isDesktop: boolean;
  onClose: () => void;
  /** Плочките се влачат към лентата под масите (не на телефон). */
  arrangeable: boolean;
  /** Ключът на плочката, която в момента се влачи — избледнява на мястото си. */
  draggedKey?: string;
  /** Влачи се бутон ОТ лентата — панелът подсказва, че може да се пусне тук. */
  draggingFromFooter: boolean;
  /** Бутонът от лентата е точно над панела. */
  isDropTarget: boolean;
}

/** Обвивката на плочка е дръжката за влачене към лентата. Недостъпната
 *  плочка е `pointer-events-none` (виж класовете на бутона), така че
 *  показалецът стига до обвивката и тя пак може да се влачи. */
const DraggableOptionTile = ({
  actionKey,
  enabled,
  dimmed,
  children,
}: {
  actionKey: string;
  enabled: boolean;
  dimmed: boolean;
  children: React.ReactNode;
}) => {
  const dragData: FooterDragData = { actionKey, source: "options" };
  const { setNodeRef, listeners } = useDraggable({
    id: `option:${actionKey}`,
    data: dragData,
    disabled: !enabled,
  });
  return (
    <div
      ref={setNodeRef}
      {...(enabled ? listeners : undefined)}
      className={`grid min-w-0 ${
        enabled ? "select-none [-webkit-touch-callout:none]" : ""
      } ${dimmed ? "opacity-40" : ""}`}
    >
      {children}
    </div>
  );
};

/**
 * Popover-ът „Опции" на футъра на масите: на телефон 2-колонен грид с
 * хоризонтални бутони, на таблет/десктоп вертикални плочки с ФИКСИРАНИ
 * колони. Решетката: на ДЕСКТОП popover-ът остава най-много на ДВА реда —
 * до 6 действия се редят на един, над това се балансират на два (без
 * самотна плочка накрая), с таван 6 колони, за да се събере и при 1024px;
 * на таблет остават 4 колони (12 действия → 3 реда).
 *
 * Извън телефон плочките се влачат към лентата под масите и обратно
 * (`footer/useFooterDrag.ts`); панелът е зоната за връщане.
 */
export const OptionsPopover = ({
  actions,
  isNarrow,
  isDesktop,
  onClose,
  arrangeable,
  draggedKey,
  draggingFromFooter,
  isDropTarget,
}: OptionsPopoverProps) => {
  const { t } = useTranslation();
  const { setNodeRef: setDropZoneRef } = useDroppable({
    id: OPTIONS_ZONE_ID,
    disabled: !arrangeable,
  });

  const isEmpty = actions.length === 0;
  const columnCount = isEmpty
    ? 2
    : isDesktop
      ? actions.length <= 6
        ? actions.length
        : Math.min(6, Math.ceil(actions.length / 2))
      : Math.min(4, actions.length);
  // При 6 колони плочката се свива, за да не се реже popover-ът на тесен десктоп.
  const tileWidth = isDesktop ? (columnCount >= 6 ? 140 : 150) : 118;

  const activate = (action: SecondaryAction) => {
    onClose();
    action.onClick?.();
  };

  // Обвивката носи подсказката на заключено действие: disabled бутон не
  // получава mouse събития, затова той е pointer-events-none, а span-ът
  // остава грид клетката.
  const withLock = (action: SecondaryAction, button: React.ReactNode) =>
    action.lockedTooltip ? (
      <Tooltip key={action.key} title={action.lockedTooltip}>
        <span className="grid min-w-0 cursor-not-allowed">{button}</span>
      </Tooltip>
    ) : (
      button
    );

  return (
    <>
      {/* Backdrop — клик извън popover-а го затваря */}
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div
        ref={setDropZoneRef}
        className={`dk-elevated absolute left-1/2 -translate-x-1/2 bg-pos-card border border-pos-line rounded-2xl z-40 ${
          isNarrow
            ? "bottom-[calc(58px+env(safe-area-inset-bottom,0px)*0.5)] grid grid-cols-2 gap-2 w-[calc(100vw-24px)] p-3 shadow-[0_16px_40px_rgba(60,40,20,.2)]"
            : `bottom-[76px] w-max grid ${
                isDesktop ? "gap-3 p-4" : "gap-2 p-3"
              } shadow-[0_16px_40px_rgba(60,40,20,.16)]`
        } ${isDropTarget ? "outline-2 outline-offset-2 outline-pos-accent" : ""}`}
        style={
          isNarrow
            ? undefined
            : {
                // Фиксирани колони → грид, ляво-подравнен (непълният последен
                // ред започва отляво). Броят и ширината са runtime стойности
                // от броя действия — затова inline style.
                gridTemplateColumns: `repeat(${columnCount}, ${tileWidth}px)`,
              }
        }
      >
        {isEmpty && (
          <div className="col-span-full flex min-h-[96px] items-center justify-center rounded-xl border-2 border-dashed border-pos-line3 px-4 text-center text-[12.5px] font-semibold leading-snug text-pos-muted">
            {t("ordering.optionsEmpty")}
          </div>
        )}
        {actions.map((action) => (
          <DraggableOptionTile
            key={action.key}
            actionKey={action.key}
            enabled={arrangeable}
            dimmed={draggedKey === action.key}
          >
            {isNarrow
              ? // Телефон: хоризонтален ред иконка + текст.
                withLock(
                  action,
                  <button
                    type="button"
                    disabled={action.disabled || !!action.lockedTooltip}
                    onClick={() => activate(action)}
                    className="relative h-[42px] w-full px-[15px] rounded-[11px] border border-pos-btn2 bg-pos-panel text-[13px] font-semibold text-pos-body inline-flex items-center justify-center gap-2 min-w-0 cursor-pointer disabled:opacity-45 disabled:cursor-not-allowed disabled:pointer-events-none"
                  >
                    {action.icon}
                    <span className="truncate">{action.label}</span>
                  </button>,
                )
              : // Десктоп/таблет: компактна заоблена кутийка — иконно кръгче
                // отгоре, етикет отдолу.
                withLock(
                  action,
                  <button
                    type="button"
                    disabled={action.disabled || !!action.lockedTooltip}
                    onClick={() => activate(action)}
                    className={`relative flex flex-col items-center rounded-2xl border border-pos-line3 bg-pos-panel cursor-pointer transition-colors hover:bg-pos-line2 disabled:opacity-45 disabled:cursor-not-allowed disabled:pointer-events-none disabled:hover:bg-pos-panel ${
                      isDesktop ? "gap-2 py-4 px-2" : "gap-1.5 py-3 px-1.5"
                    }`}
                  >
                    <span
                      className={`rounded-[13px] bg-pos-addon-bg text-pos-accent flex items-center justify-center ${
                        isDesktop
                          ? "w-11 h-11 text-[19px]"
                          : "w-10 h-10 text-[17px]"
                      }`}
                    >
                      {action.icon}
                    </span>
                    <span
                      className={`font-semibold text-pos-ink2 text-center leading-tight ${
                        isDesktop ? "text-[12.5px]" : "text-[11.5px]"
                      }`}
                    >
                      {action.label}
                    </span>
                    {action.shortcut && (
                      <KbdChip
                        shortcut={action.shortcut}
                        colorClass="text-pos-faint"
                      />
                    )}
                  </button>,
                )}
          </DraggableOptionTile>
        ))}
        {arrangeable && !isEmpty && (
          <p
            className={`col-span-full m-0 text-center text-[11.5px] leading-snug ${
              draggingFromFooter
                ? "font-semibold text-pos-accent"
                : "text-pos-muted"
            }`}
          >
            {draggingFromFooter
              ? t("ordering.optionsDropHere")
              : t("ordering.optionsDragHint")}
          </p>
        )}
      </div>
    </>
  );
};
